import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button } from '../components';
import { Users, Shield, GraduationCap, Laptop, Briefcase, Tag } from 'lucide-react';

export const CollectiviteitenPage: React.FC = () => {
  const { t } = useTranslation('pages');

  // Placeholder data - later te vervangen met echte API call
  const deals = [
    {
      id: '1',
      title: t('collectiviteiten.deal1Title'),
      type: 'verzekering',
      provider: t('collectiviteiten.deal1Provider'),
      description: t('collectiviteiten.deal1Desc'),
      regular_price: 1200,
      discounted_price: 900,
      discount_percentage: '25%',
      current_participants: 45,
      min_participants: 20,
    },
    {
      id: '2',
      title: t('collectiviteiten.deal2Title'),
      type: 'opleiding',
      provider: t('collectiviteiten.deal2Provider'),
      description: t('collectiviteiten.deal2Desc'),
      regular_price: 800,
      discounted_price: 600,
      discount_percentage: '25%',
      current_participants: 32,
      min_participants: 15,
    },
  ];

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'verzekering':
        return Shield;
      case 'opleiding':
        return GraduationCap;
      case 'software':
        return Laptop;
      case 'dienst':
        return Briefcase;
      default:
        return Tag;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'verzekering':
        return t('collectiviteiten.typeInsurance');
      case 'opleiding':
        return t('collectiviteiten.typeTraining');
      case 'software':
        return t('collectiviteiten.typeSoftware');
      case 'dienst':
        return t('collectiviteiten.typeService');
      default:
        return t('collectiviteiten.typeOther');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('collectiviteiten.title')}</h1>
        <p className="mt-2 text-gray-600">
          {t('collectiviteiten.subtitle')}
        </p>
      </div>

      {deals.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {deals.map((deal) => {
            const Icon = getTypeIcon(deal.type);
            return (
              <Card key={deal.id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <span className="text-xs font-medium text-blue-600 uppercase">
                        {getTypeLabel(deal.type)}
                      </span>
                    </div>
                  </div>
                  {deal.discount_percentage && (
                    <span className="px-2 py-1 text-xs font-bold text-white bg-green-600 rounded">
                      -{deal.discount_percentage}
                    </span>
                  )}
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {deal.title}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  {deal.description}
                </p>

                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-500">{t('collectiviteiten.provider')}:</span>
                    <span className="text-sm font-medium text-gray-900">{deal.provider}</span>
                  </div>
                  {deal.regular_price && deal.discounted_price && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">{t('collectiviteiten.regularPrice')}:</span>
                      <span className="text-sm line-through text-gray-400">
                        {'\u20AC'}{deal.regular_price.toFixed(2)}
                      </span>
                    </div>
                  )}
                  {deal.discounted_price && (
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-sm font-medium text-gray-900">{t('collectiviteiten.collectivePrice')}:</span>
                      <span className="text-lg font-bold text-green-600">
                        {'\u20AC'}{deal.discounted_price.toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mb-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{t('collectiviteiten.participants')}:</span>
                    <span className="font-medium text-gray-900">
                      {deal.current_participants}
                      {deal.min_participants && ` / ${t('collectiviteiten.min')} ${deal.min_participants}`}
                    </span>
                  </div>
                  {deal.min_participants && (
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{
                          width: `${Math.min((deal.current_participants / deal.min_participants) * 100, 100)}%`
                        }}
                      />
                    </div>
                  )}
                </div>

                <Button className="w-full">
                  {t('collectiviteiten.moreInfo')}
                </Button>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="p-12 text-center">
          <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('collectiviteiten.noDeals')}
          </h3>
          <p className="text-gray-600">
            {t('collectiviteiten.noDealsDesc')}
          </p>
        </Card>
      )}
    </div>
  );
};
